import React, { useState } from 'react';
import { uploadUserAvatar, deleteUserAvatar, validateAvatarFile } from '../services/userService';
import { 
  CloudUpload, 
  Delete, 
  Person, 
  DragIndicator 
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import '../styles/AvatarUploadManager.css';

const AvatarUploadManager = ({ 
  currentAvatar, 
  onAvatarUpdate, 
  onAvatarDelete,
  disabled = false 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileSelect = (file) => {
    if (!file) return;
    
    try {
      validateAvatarFile(file);
      
      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      handleUpload(file);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleUpload = async (file) => {
    setIsUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      const response = await uploadUserAvatar(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setSuccess('Avatar uploaded successfully!');
        setPreview(null);
        onAvatarUpdate(response.avatar);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      }, 500);
      
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to upload avatar');
      setPreview(null);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    setIsUploading(true);
    setError('');
    setSuccess('');

    try {
      await deleteUserAvatar();
      setSuccess('Avatar deleted successfully!');
      onAvatarDelete();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete avatar');
    } finally {
      setIsUploading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input
    e.target.value = '';
  };

  return (
    <div className="avatar-upload-manager">
      <div className="avatar-section">
        <h3>Profile Avatar</h3>
        <p>Upload a photo to personalize your profile</p>
        
        {/* Current Avatar Display */}
        <div className="current-avatar">
          {currentAvatar?.url || currentAvatar?.thumbnailUrl ? (
            <img 
              src={currentAvatar.thumbnailUrl || currentAvatar.url} 
              alt="Current Avatar" 
              className="avatar-image"
            />
          ) : (
            <div className="avatar-placeholder">
              <Person style={{ fontSize: 48 }} />
            </div>
          )}
        </div>

        {/* Upload Area */}
        <div 
          className={`upload-area ${dragActive ? 'drag-active' : ''} ${disabled || isUploading ? 'disabled' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isUploading && preview ? (
            <div className="upload-preview">
              <img src={preview} alt="Upload preview" className="preview-image" />
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            </div>
          ) : (
            <>
              <div className="upload-icon">
                {isUploading ? (
                  <CircularProgress size={24} className="spinner" />
                ) : (
                  <>
                    <CloudUpload style={{ fontSize: 24 }} />
                    <DragIndicator style={{ fontSize: 16 }} className="drag-indicator" />
                  </>
                )}
              </div>
              <div className="upload-text">
                <p>
                  {isUploading 
                    ? 'Uploading...' 
                    : 'Drop your avatar here or click to select'
                  }
                </p>
                <span className="upload-hint">
                  Supports JPG, PNG, WebP up to 5MB
                </span>
              </div>
            </>
          )}
          
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            onChange={handleInputChange}
            disabled={disabled || isUploading}
            className="file-input"
          />
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {(currentAvatar?.url || currentAvatar?.thumbnailUrl) && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={disabled || isUploading}
              className="delete-btn"
            >
              <Delete style={{ fontSize: 14 }} />
              Remove Avatar
            </button>
          )}
        </div>

        {/* Status Messages */}
        {error && (
          <div className="status-message error">
            {error}
          </div>
        )}
        
        {success && (
          <div className="status-message success">
            {success}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Avatar</h3>
            <p>Are you sure you want to remove your profile avatar?</p>
            <div className="modal-actions">
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete}
                className="btn-danger"
                disabled={isUploading}
              >
                {isUploading ? <CircularProgress size={14} className="spinner" /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drag Overlay */}
      {dragActive && (
        <div className="drag-overlay">
          <div className="drag-content">
            <CloudUpload style={{ fontSize: 48 }} />
            <p>Drop your avatar here</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarUploadManager; 